import { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Popover from '@mui/material/Popover';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import { useDataSmall } from 'src/context/ContextDataSmall';

type ItemProps = {
  text: string;
  icon: string;
  iconActive: string;
  href: string;
  subLink?: string[];
  sub: ItemProps[];
};

type MenuItemsProps = {
  items: ItemProps;
  menuFull: boolean;
};

const badgeStyle = {
  '&.MuiBadge-root .MuiBadge-badge': {
    color: 'primary.light',
    bgcolor: '#FC6E51',
    right: '-10px',
    top: '5px',
  },
};

const MenuItems: React.FC<MenuItemsProps> = (props) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dataSmallContext = useDataSmall();
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const openPopover = Boolean(anchorEl);
  const id = openPopover ? 'simple-popover' : undefined;

  const handleIconParentClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleIconClick = (href: string) => {
    navigate(href);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const handleParentClick = () => {
    setOpen(!open);
  };

  const checkIsParent = () => {
    return props.items.sub.length > 0;
  };

  const checkMenuActive = (href: string) => {
    return href === location.pathname;
  };

  const checkChildMenuActive = () => {
    return props.items.subLink?.includes(location.pathname) || false;
  };

  const listItemText = (item: any) => {
    return (
      <ListItemText
        primary={item.text}
        sx={{
          fontWeight: checkMenuActive(item.href) ? 700 : 500,
          color: checkMenuActive(item.href)
            ? '#3061C2'
            : 'secondary.contrastText',
          overflow: 'hidden',
        }}
      />
    );
  };

  return props.menuFull ? (
    <>
      <ListItem
        onClick={() => (checkIsParent() ? handleParentClick() : null)}
        disableGutters
        sx={{
          p: '6px 12px 6px 12px',
          borderRadius: '4px',
          '&.MuiListItem-root.active': { backgroundColor: '#E8F8FF' },
          '&.MuiListItem-root:hover': { backgroundColor: '#E8F8FF' },
        }}
        component={RouterLink}
        to={props.items.href || '#'}
        className={checkMenuActive(props.items.href) ? 'active' : ''}
      >
        <ListItemIcon sx={{ minWidth: '36px' }}>
          <Box
            component='img'
            src={
              checkMenuActive(props.items.href) || checkChildMenuActive()
                ? props.items.iconActive
                : props.items.icon
            }
            alt={props.items.text}
            my={'4px'}
            width={'24px'}
            height={'24px'}
          />
        </ListItemIcon>
        {props.items.text === '会計' ? (
          <Badge
            badgeContent={dataSmallContext.badgeCountPayment}
            sx={badgeStyle}
          >
            <ListItemText
              primary={props.items.text}
              sx={{
                fontWeight:
                  checkMenuActive(props.items.href) || checkChildMenuActive()
                    ? 700
                    : 500,
                color:
                  checkMenuActive(props.items.href) || checkChildMenuActive()
                    ? '#3061C2'
                    : 'secondary.contrastText',
              }}
            />
          </Badge>
        ) : (
          <ListItemText
            primary={props.items.text}
            sx={{
              fontWeight:
                checkMenuActive(props.items.href) || checkChildMenuActive()
                  ? 700
                  : 500,
              color:
                checkMenuActive(props.items.href) || checkChildMenuActive()
                  ? '#3061C2'
                  : 'secondary.contrastText',
            }}
          />
        )}
        {checkIsParent() && (
          <Box
            component={'img'}
            src={`/images/icons/ic-arrow-${open ? 'up' : 'down'}.svg`}
            alt='icon up'
            position={'absolute'}
            right={'12px'}
          />
        )}
      </ListItem>
      {checkIsParent() && (
        <Collapse in={open} timeout='auto' unmountOnExit>
          <List component='div' disablePadding>
            {props.items.sub.map((item, index) => {
              return (
                <ListItem
                  sx={{
                    p: '6px 15px 6px 40px',
                    minWidth: '36px',
                    borderRadius: '4px',
                    '&.MuiListItem-root.active': { backgroundColor: '#E8F8FF' },
                    '&.MuiListItem-root:hover': { backgroundColor: '#E8F8FF' },
                  }}
                  key={index}
                  component={RouterLink}
                  to={item.href || '#'}
                  className={checkMenuActive(item.href) ? 'active' : ''}
                >
                  <ListItemIcon sx={{ minWidth: '36px' }}>
                    <Box
                      component='img'
                      src={
                        checkMenuActive(item.href) ? item.iconActive : item.icon
                      }
                      alt={item.text}
                      my={'4px'}
                      width={'24px'}
                      height={'24px'}
                    />
                  </ListItemIcon>
                  {item.text === '未納/収納' ? (
                    <Badge
                      badgeContent={dataSmallContext.badgeCountPayment}
                      sx={badgeStyle}
                    >
                      {listItemText(item)}
                    </Badge>
                  ) : (
                    listItemText(item)
                  )}
                </ListItem>
              );
            })}
          </List>
        </Collapse>
      )}
    </>
  ) : (
    <>
      <ListItem
        disableGutters
        sx={{
          p: 0,
          borderRadius: '4px',
          cursor: 'pointer',
          '&.MuiListItem-root.active': { backgroundColor: '#E8F8FF' },
          '&.MuiListItem-root:hover': { backgroundColor: '#E8F8FF' },
        }}
        className={checkMenuActive(props.items.href) ? 'active' : ''}
      >
        <Tooltip title={props.items.text} arrow placement='left'>
          <Button
            aria-describedby={id}
            onClick={(event) =>
              checkIsParent()
                ? handleIconParentClick(event)
                : handleIconClick(props.items.href)
            }
            sx={{
              p: '6px 12px 6px 12px',
              minWidth: '24px',
            }}
          >
            {props.items.text === '会計' ? (
              <Badge
                badgeContent={dataSmallContext.badgeCountPayment}
                sx={badgeStyle}
              >
                <Box
                  component='img'
                  src={
                    checkMenuActive(props.items.href) || checkChildMenuActive()
                      ? props.items.iconActive
                      : props.items.icon
                  }
                  alt={props.items.text}
                  my={'4px'}
                  width={'24px'}
                  height={'24px'}
                />
              </Badge>
            ) : (
              <Box
                component='img'
                src={
                  checkMenuActive(props.items.href) || checkChildMenuActive()
                    ? props.items.iconActive
                    : props.items.icon
                }
                alt={props.items.text}
                my={'4px'}
                width={'24px'}
                height={'24px'}
              />
            )}
          </Button>
        </Tooltip>
        <Popover
          id={id}
          open={openPopover}
          anchorEl={anchorEl}
          onClose={handlePopoverClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: -17,
          }}
        >
          <List component='div' disablePadding>
            {props.items.sub.map((item, index) => {
              return (
                <ListItem
                  sx={{
                    p: '6px 30px 6px 16px',
                    minWidth: '36px',
                    borderRadius: '4px',
                    '&.MuiListItem-root.active': { backgroundColor: '#E8F8FF' },
                    '&.MuiListItem-root:hover': { backgroundColor: '#E8F8FF' },
                  }}
                  key={index}
                  component={RouterLink}
                  to={item.href || '#'}
                  onClick={handlePopoverClose}
                  className={checkMenuActive(item.href) ? 'active' : ''}
                >
                  <ListItemIcon sx={{ minWidth: '36px' }}>
                    <Box
                      component='img'
                      src={
                        checkMenuActive(item.href) ? item.iconActive : item.icon
                      }
                      alt={item.text}
                      my={'4px'}
                      width={'24px'}
                      height={'24px'}
                    />
                  </ListItemIcon>
                  {item.text === '未納/収納' ? (
                    <Badge
                      badgeContent={dataSmallContext.badgeCountPayment}
                      sx={badgeStyle}
                    >
                      {listItemText(item)}
                    </Badge>
                  ) : (
                    listItemText(item)
                  )}
                </ListItem>
              );
            })}
          </List>
        </Popover>
      </ListItem>
    </>
  );
};

export default MenuItems;
